// src/departamentos/OrganigramaDepartamentos.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
} from "@mui/material";

const authOnly = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const getAvatarUrl = (usuario) => {
  if (!usuario?.avatar_url) return "";
  return `${Global.url}${usuario.avatar_url}`;
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();
  const n1 = nombre ? nombre.split(" ")[0]?.[0] || "" : "";
  const a1 = apellidos ? apellidos.split(" ")[0]?.[0] || "" : "";
  const combo = (n1 + a1).toUpperCase();
  if (combo) return combo;
  if (nombre) return nombre[0].toUpperCase();
  return "?";
};

const normalizeRol = (rol) => (rol || "miembro").toLowerCase();

const rolOrder = (rol) => {
  const r = normalizeRol(rol);
  if (r === "responsable") return 0;
  if (r === "backup") return 1;
  return 2; // miembro y otros
};

const rolLabel = (rol) => {
  const r = normalizeRol(rol);
  if (r === "responsable") return "Responsable";
  if (r === "backup") return "Backup";
  return "Miembros";
};

const UserPill = ({ u }) => {
  const src = getAvatarUrl(u);
  const inactive = u?.miembro_activo === false;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        pr: 1.25,
        pl: 0.75,
        py: 0.5,
        borderRadius: 999,
        border: "1px solid rgba(0,0,0,0.10)",
        backgroundColor: "#fff",
        opacity: inactive ? 0.55 : 1,
      }}
      title={`${u?.nombre || ""} ${u?.apellidos || ""}`.trim()}
    >
      <Avatar src={src || undefined} sx={{ width: 26, height: 26 }}>
        {!src ? getIniciales(u) : null}
      </Avatar>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {`${u?.nombre || ""} ${u?.apellidos || ""}`.trim() || "—"}
      </Typography>
    </Box>
  );
};

export const OrganigramaDepartamentos = () => {
  const toast = useToast();

  // =========================
  // State: Data
  // =========================
  const [departamentos, setDepartamentos] = useState([]);
  const [usuariosPorDepto, setUsuariosPorDepto] = useState({}); // { [idDepto]: usuarios[] }

  // =========================
  // State: Loading
  // =========================
  const [loading, setLoading] = useState(true);

  // =========================
  // Load: departamentos + miembros (todo del tirón)
  // =========================
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // 1) Departamentos
        const resDeptos = await fetch(`${Global.url}departamentos?incluirInactivos=true`, {
          headers: authOnly(),
        });

        if (!resDeptos.ok) {
          toast.error?.("No se pudieron cargar los departamentos.");
          setDepartamentos([]);
          setUsuariosPorDepto({});
          return;
        }

        const deptosData = await resDeptos.json().catch(() => []);
        const deptos = Array.isArray(deptosData) ? deptosData : [];
        setDepartamentos(deptos);

        // 2) Usuarios por departamento (paralelo)
        const pairs = await Promise.all(
          deptos.map(async (d) => {
            const id = Number(d.id);
            if (!id) return [null, []];

            try {
              const resU = await fetch(
                `${Global.url}departamentos/${id}/usuarios?incluirInactivos=true`,
                { headers: authOnly() }
              );

              if (!resU.ok) return [id, []];

              const uData = await resU.json().catch(() => []);
              return [id, Array.isArray(uData) ? uData : []];
            } catch {
              return [id, []];
            }
          })
        );

        const map = {};
        for (const [id, users] of pairs) {
          if (id != null) map[id] = users;
        }
        setUsuariosPorDepto(map);
      } catch (e) {
        console.error("Error cargando organigrama departamentos:", e);
        toast.error?.("Error de conexión al cargar el organigrama.");
        setDepartamentos([]);
        setUsuariosPorDepto({});
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // View model: deptos ordenados + agrupación por rol
  // =========================
  const deptosVM = useMemo(() => {
    return (departamentos || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map((d) => {
        const id = Number(d.id);
        const usuarios = usuariosPorDepto[id] || [];

        const activos = usuarios.filter((u) => u?.miembro_activo !== false);
        const inactivos = usuarios.filter((u) => u?.miembro_activo === false);

        const all = [...activos, ...inactivos].slice().sort((a, b) => {
          const ro = rolOrder(a?.rol) - rolOrder(b?.rol);
          if (ro !== 0) return ro;
          return `${a?.nombre || ""} ${a?.apellidos || ""}`.localeCompare(
            `${b?.nombre || ""} ${b?.apellidos || ""}`
          );
        });

        const responsables = all.filter((u) => normalizeRol(u?.rol) === "responsable");
        const backups = all.filter((u) => normalizeRol(u?.rol) === "backup");
        const miembros = all.filter(
          (u) => normalizeRol(u?.rol) !== "responsable" && normalizeRol(u?.rol) !== "backup"
        );

        return {
          id,
          nombre: d?.nombre || `Departamento #${id}`,
          descripcion: d?.descripcion || "",
          counts: {
            total: usuarios.length,
            activos: activos.length,
            inactivos: inactivos.length,
          },
          grupos: [
            { key: "responsable", label: rolLabel("responsable"), users: responsables },
            { key: "backup", label: rolLabel("backup"), users: backups },
            { key: "miembro", label: rolLabel("miembro"), users: miembros },
          ].filter((g) => g.users.length > 0),
        };
      });
  }, [departamentos, usuariosPorDepto]);

    // =========================
    // Render
    // =========================
    return (
    <Box className="appContainer appContainer--wide">
        <Stack spacing={2}>
        {/* Header */}
        <Box className="pageHeader">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Organigrama de departamentos
            </Typography>
            <Typography className="pageHeader__subtitle">
            Vista rápida de pertenencias por departamento (responsables, backups y miembros).
            </Typography>
        </Box>

        {/* Content */}
        <Box className="contentContainer contentContainer--full">
            <Paper sx={{ p: 2 }}>
            {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}>
                <CircularProgress size={20} />
                <Typography sx={{ color: "text.secondary" }}>Cargando…</Typography>
                </Box>
            ) : deptosVM.length === 0 ? (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                No hay departamentos que mostrar.
                </Box>
            ) : (
                <Box
                sx={{
                    display: "grid",
                    gap: 12, // 🔽 menos separación
                    gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    lg: "1fr 1fr 1fr",
                    },
                    alignItems: "start",
                }}
                >
                {deptosVM.map((d) => (
                    <Paper
                    key={d.id}
                    sx={{
                        p: 0,
                        backgroundColor: "#ffffff", // 🔑 contraste
                        borderRadius: 2,
                        overflow: "hidden",
                    }}
                    >
                    {/* Cabecera azul */}
                    <Box
                        sx={{
                        px: 2,
                        py: 1.25,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        }}
                    >
                        <Typography sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                        {d.nombre}
                        </Typography>

                        <Chip
                        label={`${d.counts.activos}/${d.counts.total}`}
                        size="small"
                        sx={{
                            backgroundColor: "rgba(255,255,255,0.22)",
                            color: "primary.contrastText",
                            fontWeight: 800,
                        }}
                        />
                    </Box>

                    {/* Body */}
                    <Box sx={{ p: 2 }}>
                        <Stack spacing={1.25}>
                        {d.descripcion ? (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {d.descripcion}
                            </Typography>
                        ) : null}

                        <Divider />

                        {d.grupos.length === 0 ? (
                            <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", fontStyle: "italic" }}
                            >
                            Sin miembros asignados.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                            {d.grupos.map((g) => (
                                <Stack key={g.key} spacing={0.75}>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                    {g.label}
                                </Typography>

                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {g.users.map((u) => (
                                    <UserPill key={u.id} u={u} />
                                    ))}
                                </Box>
                                </Stack>
                            ))}
                            </Stack>
                        )}
                        </Stack>
                    </Box>
                    </Paper>
                ))}
                </Box>
            )}
            </Paper>
        </Box>
        </Stack>
    </Box>
    );

};

export default OrganigramaDepartamentos;
